/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch } from 'react-redux';
import { CRM_OTHER_DETAILS_MAX_LENGTH } from '../../../common/config';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import {
    isKeyInProductApplications,
    isKeyInProductApplicationsAtIndex,
} from '../../../lib/briefing';
import { noOp } from '../../../lib/utils';
import {
    ProductApplications,
    StepProps,
    ValueOrNotApplicable,
} from '../../../types';
import { useBriefingState } from '../BriefingState';
import {
    ProcessingCapabilitiesFields,
    processingCapabilityFieldDependencies,
} from './fieldDependencies';

export const isFieldAvailableDependingOnApplications = (
  productApplications: ProductApplications,
  fieldName: ProcessingCapabilitiesFields,
): boolean => {
  const dependencyApplications =
    processingCapabilityFieldDependencies[fieldName];

  // Include all fields which don't have a dependency definition or the definition is an empty array
  if (!dependencyApplications || dependencyApplications.length === 0) {
    return true;
  }
  const deepestSelectedApplicationLevel = productApplications.slice(-1);
  // If "Other" has been selected...
  if (isKeyInProductApplications('other', deepestSelectedApplicationLevel)) {
    // Include if "Other" has been selected as the top-level application
    if (productApplications.length === 1) {
      return true;
    } else {
      // Include if the option has our last-but-one application (the one before "Other") as a dependency
      return dependencyApplications.some((dependencyApplication) =>
        isKeyInProductApplicationsAtIndex(
          dependencyApplication,
          productApplications,
          productApplications.length - 2,
        ),
      );
    }
  } else {
    // Include if the option has our last application as a dependency
    return dependencyApplications.some((dependencyApplication) =>
      isKeyInProductApplications(
        dependencyApplication,
        deepestSelectedApplicationLevel,
      ),
    );
  }
};

export const StepProcessingCapabilities: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [
    {
      productApplications,
      bakeStability,
      bakingTime,
      bakingTemperature,
      mixingStepDetails,
      productPh,
      brix,
      fillingTime,
      fillingTemperature,
      otherProcessingCapabilities,
      processingCapabilitiesPackagingDetails,
      foodSafetyDetails,
      processingHotCold,
      homogenisation,
    },
    {
      setBakeStability,
      setBakingTime,
      setBakingTemperature,
      setMixingStepDetails,
      setProductPh,
      setBrix,
      setFillingTime,
      setFillingTemperature,
      setOtherProcessingCapabilities,
      setProcessingCapabilitiesPackagingDetails,
      setFoodSafetyDetails,
      setProcessingHotCold,
      setHomogenisation,
    },
  ] = useBriefingState();

  const [areFieldsValid, setAreFieldsValid] = useState<
    Partial<Record<ProcessingCapabilitiesFields, boolean>>
  >({});
  const isStepValid = useMemo(
    () => !Object.values(areFieldsValid).some((field) => !field),
    [areFieldsValid],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const onFieldValidate = useCallback(
    (key: ProcessingCapabilitiesFields, isValid: boolean) => {
      if (areFieldsValid[key] === isValid) return;

      setAreFieldsValid({
        ...areFieldsValid,
        [key]: isValid,
      });
    },
    [areFieldsValid],
  );

  const handleInputValues = useCallback(
    (
      stateVariable: ValueOrNotApplicable | null,
      reducerFunction: ActionCreatorWithPayload<any>,
      label: ProcessingCapabilitiesFields,
    ) => {
      if (stateVariable === false) {
        return (
          <Input
            placeholder={t('placeholders:notApplicable')}
            disabled={true}
          />
        );
      } else {
        return (
          <Input
            initialValue={stateVariable}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              dispatch(reducerFunction(e.target.value));
            }}
            onValidate={(isValid) => onFieldValidate(label, isValid)}
            maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
          />
        );
      }
    },
    [dispatch, onFieldValidate, t],
  );

  const handleCheckboxes = useCallback(
    (
      stateVariable: ValueOrNotApplicable | null,
      reducerFunction: ActionCreatorWithPayload<any>,
    ) => {
      return (
        <div className="flex ml-4 xs:mt-1 md:mt-2 font-regular">
          <Checkbox
            initialChecked={stateVariable === false}
            label={t('placeholders:notApplicable')}
            value={t<string>('placeholders:notApplicable')}
            onChange={(e) => {
              if (e) {
                dispatch(reducerFunction(!e));
              } else {
                dispatch(reducerFunction(null));
              }
            }}
          />
        </div>
      );
    },
    [dispatch, t],
  );

  const handleCheckboxesForPairedInputs = useCallback(
    (
      stateVariableOne: ValueOrNotApplicable | null,
      reducerFunctionOne: ActionCreatorWithPayload<ValueOrNotApplicable | null>,
      stateVariableTwo: ValueOrNotApplicable | null,
      reducerFunctionTwo: ActionCreatorWithPayload<ValueOrNotApplicable | null>,
    ) => (
      <div className="flex h-full ml-4 xs:mt-1 md:mt-6 font-regular">
        <Checkbox
          initialChecked={
            stateVariableOne === false && stateVariableTwo === false
              ? true
              : false
          }
          label={t('placeholders:notApplicable')}
          value={t<string>('placeholders:notApplicable')}
          onChange={(choice) => {
            const checked = choice === true ? !choice : null;
            batch(() => {
              dispatch(reducerFunctionOne(checked));
              dispatch(reducerFunctionTwo(checked));
            });
          }}
        />
      </div>
    ),
    [dispatch, t],
  );

  const renderSingleInputMarkUp = useCallback(
    (
      stateVariable: ValueOrNotApplicable | null,
      reducerFunction: ActionCreatorWithPayload<any>,
      label: ProcessingCapabilitiesFields,
    ) => (
      <TemplateFlexGridContainer.BriefingStepSection spaceY={undefined}>
        <InputGroup label={t(`topics:processing-capabilities:${label}`)}>
          <div className="flex">
            <div className='sm:max-w-[550px] w-full mr-4'>
              {handleInputValues(stateVariable, reducerFunction, label)}
            </div>
            {label !== 'otherProcessingCapabilities' &&
              handleCheckboxes(stateVariable, reducerFunction)}
          </div>
        </InputGroup>
      </TemplateFlexGridContainer.BriefingStepSection>
    ),
    [handleCheckboxes, handleInputValues, t],
  );

  const renderPairedInputsMarkUp = useCallback(
    (
      stateVariableOne: ValueOrNotApplicable | null,
      reducerFunctionOne: ActionCreatorWithPayload<ValueOrNotApplicable | null>,
      stateVariableTwo: ValueOrNotApplicable | null,
      reducerFunctionTwo: ActionCreatorWithPayload<ValueOrNotApplicable | null>,
      field: 'baking' | 'filling',
    ) => {
      if (stateVariableOne === false && stateVariableTwo === false) {
        // {''} below is needed for the state to dispatch and render correct markup for some reason
        return (
          <TemplateFlexGridContainer.BriefingStepSection spaceY={undefined}>
            <div className="flex">
              <div className="flex flex-row justify-between mr-4 w-full sm:max-w-[550px]">
                <div className="flex flex-col justify-end w-5/12">
                  <InputGroup
                    label={t(`topics:processing-capabilities:${field}Time`)}
                  >
                    <Input
                      placeholder={t('placeholders:notApplicable')}
                      disabled={true}
                    />
                  </InputGroup>
                </div>
                <div className="flex flex-col justify-center flex-1">
                  <div className="flex flex-1" />
                  <div className="flex flex-col justify-center flex-1 h-full">
                    <div className="border-b border-gray-500 md:-mt-1" />
                  </div>
                </div>
                <div className="flex flex-col justify-end w-5/12">
                  <InputGroup
                    label={t(
                      `topics:processing-capabilities:${field}Temperature`,
                    )}
                  >
                    <Input
                      placeholder={t('placeholders:notApplicable')}
                      disabled={true}
                    />
                  </InputGroup>
                </div>
              </div>
              <div className="flex flex-col content-center justify-center">
                <div className="flex h-full">{''}</div>
                {handleCheckboxesForPairedInputs(
                  stateVariableOne,
                  reducerFunctionOne,
                  stateVariableTwo,
                  reducerFunctionTwo,
                )}
              </div>
            </div>
          </TemplateFlexGridContainer.BriefingStepSection>
        );
      } else {
        return (
          <TemplateFlexGridContainer.BriefingStepSection spaceY={undefined}>
            <div className="flex">
              <div className="flex flex-row justify-between mr-4 w-full sm:max-w-[550px]">
                <div className="flex flex-col justify-end w-5/12">
                  <InputGroup
                    label={t(`topics:processing-capabilities:${field}Time`)}
                  >
                    {handleInputValues(
                      stateVariableOne,
                      reducerFunctionOne,
                      `${field}Time` as ProcessingCapabilitiesFields,
                    )}
                  </InputGroup>
                </div>
                <div className="flex flex-col justify-center flex-1">
                  <div className="flex flex-1" />
                  <div className="flex flex-col justify-center flex-1 h-full">
                    <div className="border-b border-gray-500 md:-mt-1" />
                  </div>
                </div>
                <div className="flex flex-col justify-end w-5/12">
                  <InputGroup
                    label={t(
                      `topics:processing-capabilities:${field}Temperature`,
                    )}
                  >
                    {handleInputValues(
                      stateVariableTwo,
                      reducerFunctionTwo,
                      `${field}Temperature` as ProcessingCapabilitiesFields,
                    )}
                  </InputGroup>
                </div>
              </div>
              <div className="flex flex-col content-center justify-center">
                <div className="flex h-full">{''}</div>
                {handleCheckboxesForPairedInputs(
                  stateVariableOne,
                  reducerFunctionOne,
                  stateVariableTwo,
                  reducerFunctionTwo,
                )}
              </div>
            </div>
          </TemplateFlexGridContainer.BriefingStepSection>
        );
      }
    },
    [t, handleInputValues, handleCheckboxesForPairedInputs],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:processing-capabilities:heading:general`)}
        optionalField={mandatoryFields['processing-capabilities']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody spaceY={5}>
        <div className="flex flex-grow flex-wrap xl:max-w-6xl">
          <div className="w-full">
            <div className="flex flex-col space-y-5">
              {isFieldAvailableDependingOnApplications(
                productApplications,
                'bakeStability',
              ) &&
                renderSingleInputMarkUp(
                  bakeStability,
                  setBakeStability,
                  'bakeStability',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'bakingTime',
              ) &&
                isFieldAvailableDependingOnApplications(
                  productApplications,
                  'bakingTemperature',
                ) &&
                renderPairedInputsMarkUp(
                  bakingTime,
                  setBakingTime,
                  bakingTemperature,
                  setBakingTemperature,
                  'baking',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'processingCapabilitiesPackagingDetails',
              ) &&
                renderSingleInputMarkUp(
                  processingCapabilitiesPackagingDetails,
                  setProcessingCapabilitiesPackagingDetails,
                  'processingCapabilitiesPackagingDetails',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'mixingStepDetails',
              ) &&
                renderSingleInputMarkUp(
                  mixingStepDetails,
                  setMixingStepDetails,
                  'mixingStepDetails',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'foodSafetyDetails',
              ) &&
                renderSingleInputMarkUp(
                  foodSafetyDetails,
                  setFoodSafetyDetails,
                  'foodSafetyDetails',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'productPh',
              ) &&
                renderSingleInputMarkUp(productPh, setProductPh, 'productPh')}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'processingHotCold',
              ) &&
                renderSingleInputMarkUp(
                  processingHotCold,
                  setProcessingHotCold,
                  'processingHotCold',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'homogenisation',
              ) &&
                renderSingleInputMarkUp(
                  homogenisation,
                  setHomogenisation,
                  'homogenisation',
                )}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'brix',
              ) && renderSingleInputMarkUp(brix, setBrix, 'brix')}

              {isFieldAvailableDependingOnApplications(
                productApplications,
                'fillingTime',
              ) &&
                isFieldAvailableDependingOnApplications(
                  productApplications,
                  'fillingTemperature',
                ) &&
                renderPairedInputsMarkUp(
                  fillingTime,
                  setFillingTime,
                  fillingTemperature,
                  setFillingTemperature,
                  'filling',
                )}

              {renderSingleInputMarkUp(
                otherProcessingCapabilities,
                setOtherProcessingCapabilities,
                'otherProcessingCapabilities',
              )}
            </div>
          </div>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
